import { useState, useRef } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Download, Upload, FileText, FileJson, MoreVertical } from 'lucide-react'

interface ExportImportButtonsProps {
  onExportText: () => void
  onExportJSON: () => void
  onImportJSON: (jsonString: string) => void
  dataTypeName: string // 例: "名前", "プロット", "タイトル" など
  disabled?: boolean
}

export function ExportImportButtons({
  onExportText,
  onExportJSON,
  onImportJSON,
  dataTypeName,
  disabled = false
}: ExportImportButtonsProps) {
  const [isImporting, setIsImporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsImporting(true)
    try {
      const text = await file.text()
      onImportJSON(text)
    } catch (error) {
      console.error('Failed to read file:', error)
      alert('ファイルの読み込みに失敗しました。')
    } finally {
      setIsImporting(false)
      // ファイル選択をリセット
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <MoreVertical className="h-4 w-4" />
            <span className="ml-2">データ管理</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48" style={{ backgroundColor: 'white', color: 'black' }}>
          <DropdownMenuItem onClick={onExportText} disabled={disabled} className="hover:bg-gray-100">
            <FileText className="h-4 w-4 mr-2" />
            テキスト形式でエクスポート
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onExportJSON} disabled={disabled} className="hover:bg-gray-100">
            <FileJson className="h-4 w-4 mr-2" />
            JSON形式でエクスポート
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-gray-200" />
          <DropdownMenuItem 
            onClick={handleImportClick} 
            disabled={isImporting}
            className="hover:bg-gray-100"
          >
            <Upload className="h-4 w-4 mr-2" />
            {isImporting ? 'インポート中...' : `${dataTypeName}をインポート`}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

// コンパクト版（アイコンのみ）
export function ExportImportButtonsCompact({
  onExportText,
  onExportJSON,
  onImportJSON,
  dataTypeName,
  disabled = false
}: ExportImportButtonsProps) {
  const [isImporting, setIsImporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsImporting(true)
    try {
      const text = await file.text()
      onImportJSON(text)
    } catch (error) {
      console.error('Failed to read file:', error)
      alert('ファイルの読み込みに失敗しました。')
    } finally {
      setIsImporting(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={onExportText}
          disabled={disabled}
          title="テキスト形式でエクスポート"
        >
          <FileText className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onExportJSON}
          disabled={disabled}
          title="JSON形式でエクスポート"
        >
          <FileJson className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleImportClick}
          disabled={disabled || isImporting}
          title={`${dataTypeName}をインポート`}
        >
          <Upload className="h-4 w-4" />
        </Button>
      </div>
    </>
  )
}